import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, TextField, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Switch, CircularProgress, Alert, Box,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '@/api/fetchApi';

export interface EditEntitlementDialogProps {
  tenantId: string;
  domainId: string;
  domainName: string;
  currentStatus: string;
  currentExpiresAt?: string | null;
  currentPaid?: boolean;
  open: boolean;
  onClose: () => void;
  onUpdated?: () => void;
}

const STATUS_OPTIONS = ['Active', 'Trial', 'Suspended', 'Expired'];

export function EditEntitlementDialog({
  tenantId, domainId, domainName, currentStatus,
  currentExpiresAt, currentPaid,
  open, onClose, onUpdated,
}: EditEntitlementDialogProps) {
  const [status, setStatus] = useState(currentStatus);
  const [expiresAt, setExpiresAt] = useState('');
  const [paid, setPaid] = useState(currentPaid ?? false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      setStatus(currentStatus);
      setExpiresAt(currentExpiresAt ? currentExpiresAt.slice(0, 10) : '');
      setPaid(currentPaid ?? false);
    }
  }, [open, currentStatus, currentExpiresAt, currentPaid]);

  const updateMutation = useMutation({
    mutationFn: () => fetchApi(`/domains/tenants/${tenantId}/entitlements/${domainId}`, {
      method: 'PATCH',
      body: JSON.stringify({
        status,
        expiresAt: expiresAt || null,
        paid,
      }),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entitlements', tenantId] });
      onUpdated?.();
      onClose();
    },
  });

  const hasChanges = status !== currentStatus
    || (expiresAt || null) !== (currentExpiresAt ? currentExpiresAt.slice(0, 10) : null)
    || paid !== (currentPaid ?? false);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Entitlement</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>
          Edit entitlement for <strong>{domainName}</strong> ({domainId}).
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Expiry date"
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            size="small"
            fullWidth
            slotProps={{ inputLabel: { shrink: true } }}
            helperText="Leave empty for perpetual entitlement"
          />

          <FormControlLabel
            control={<Switch checked={paid} onChange={(e) => setPaid(e.target.checked)} />}
            label="Paid entitlement"
          />
        </Box>

        {updateMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>Update failed. Please try again.</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => updateMutation.mutate()}
          disabled={updateMutation.isPending || !hasChanges}
          startIcon={updateMutation.isPending ? <CircularProgress size={16} /> : undefined}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
